import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Container,
    Header,
    Button,
    Modal,
    Form,
    Message,
    Tab,
    Icon,
} from 'semantic-ui-react';
import { isICCSupport, isReadOnly } from '../auth';
import { Footer } from '../footer';
import { LoadingWrapper, ReactTable } from '../common';
import {
    getAssetMakes,
    createAssetMake,
    updateAssetMake,
    deleteAssetMake,
    getAssetModels,
    createAssetModel,
    updateAssetModel,
    deleteAssetModel,
} from './assetMakeModelService';

const EMPTY_MAKE_FORM = { name: '' };
const EMPTY_MODEL_FORM = { name: '', make: '' };

const extractFieldErrors = (errData, fields) => {
    const errors = {};
    fields.forEach((field) => {
        if (errData[field]) {
            errors[field] = Array.isArray(errData[field]) ? errData[field][0] : errData[field];
        }
    });
    return errors;
};

const headerRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.75rem',
    marginBottom: '1rem',
    marginTop: '1rem',
};

const AssetMakesPane = ({ makes, models, loading, error, canEdit, onChanged }) => {
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editMake, setEditMake] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [makeToDelete, setMakeToDelete] = useState(null);

    const [formData, setFormData] = useState(EMPTY_MAKE_FORM);
    const [formErrors, setFormErrors] = useState({});
    const [modalError, setModalError] = useState('');
    const [saving, setSaving] = useState(false);

    const handleFormChange = useCallback((field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }, []);

    const validateForm = (data) => {
        const errors = {};
        if (!data.name.trim()) errors.name = 'Name is required';
        return errors;
    };

    const openCreate = () => {
        setFormData(EMPTY_MAKE_FORM);
        setFormErrors({});
        setModalError('');
        setCreateOpen(true);
    };

    const handleCreate = async () => {
        const errors = validateForm(formData);
        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }
        setSaving(true);
        setModalError('');
        try {
            const resp = await createAssetMake(formData);
            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                setFormErrors(extractFieldErrors(errData, ['name']));
                setModalError(errData.non_field_errors?.[0] || errData.detail || '');
                return;
            }
            await onChanged();
            setCreateOpen(false);
        } catch {
            setModalError('Failed to create make. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const openEdit = (make) => {
        setFormData({ name: make.name });
        setFormErrors({});
        setModalError('');
        setEditMake(make);
        setEditOpen(true);
    };

    const closeEdit = useCallback(() => {
        setEditOpen(false);
        setEditMake(null);
    }, []);

    const handleEdit = async () => {
        const errors = validateForm(formData);
        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }
        setSaving(true);
        setModalError('');
        try {
            const resp = await updateAssetMake(editMake.id, formData);
            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                setFormErrors(extractFieldErrors(errData, ['name']));
                setModalError(errData.non_field_errors?.[0] || errData.detail || '');
                return;
            }
            await onChanged();
            closeEdit();
        } catch {
            setModalError('Failed to update make. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const openDelete = (make) => {
        setModalError('');
        setMakeToDelete(make);
        setDeleteOpen(true);
    };

    const handleDelete = async () => {
        if (!makeToDelete) return;
        setSaving(true);
        try {
            const resp = await deleteAssetMake(makeToDelete.id);
            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                setModalError(errData.detail || 'Failed to delete make.');
                return;
            }
            await onChanged();
            setDeleteOpen(false);
            setMakeToDelete(null);
        } catch {
            setModalError('Failed to delete make. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const columns = useMemo(() => {
        const cols = [
            {
                Header: 'Make',
                accessor: 'name',
                Cell: ({ value }) => <strong>{value}</strong>,
            },
            {
                Header: 'Models',
                id: 'modelCount',
                accessor: (make) => models.filter((m) => m.make === make.id).length,
                Cell: ({ value }) => <div style={{ textAlign: 'center' }}>{value}</div>,
            },
        ];

        if (canEdit) {
            cols.push({
                Header: 'Actions',
                id: 'actions',
                disableFilters: true,
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Button
                            size="small"
                            icon="edit"
                            aria-label={`Edit ${row.original.name}`}
                            title="Edit make"
                            onClick={() => openEdit(row.original)}
                        />
                        <Button
                            size="small"
                            icon="trash"
                            color="red"
                            aria-label={`Delete ${row.original.name}`}
                            title="Delete make"
                            onClick={() => openDelete(row.original)}
                        />
                    </div>
                ),
            });
        }

        return cols;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [models, canEdit]);

    return (
        <>
            <div style={headerRowStyle}>
                <Header as="h3" style={{ margin: 0 }}>Makes</Header>
                {canEdit && (
                    <Button primary icon labelPosition="left" onClick={openCreate}>
                        <Icon name="plus" />
                        Add Make
                    </Button>
                )}
            </div>

            {error && <Message negative content={error} />}

            <LoadingWrapper loading={loading} text="Loading makes...">
                {makes.length === 0 ? (
                    <Message info content="No makes found." />
                ) : (
                    <ReactTable
                        className="asset-makes-table react-table-mobile-compact"
                        data={makes}
                        columns={columns}
                        hideGlobalFilter
                    />
                )}
            </LoadingWrapper>

            <Modal open={createOpen} onClose={() => setCreateOpen(false)} size="small">
                <Modal.Header>Add Make</Modal.Header>
                <Modal.Content>
                    <Form>
                        {modalError && <Message negative content={modalError} />}
                        <Form.Input
                            label="Name"
                            required
                            placeholder="e.g. Ford"
                            value={formData.name}
                            error={formErrors.name ? { content: formErrors.name } : false}
                            onChange={(_, { value }) => handleFormChange('name', value)}
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
                    <Button primary onClick={handleCreate} loading={saving} disabled={saving}>Create</Button>
                </Modal.Actions>
            </Modal>

            <Modal open={editOpen} onClose={closeEdit} size="small">
                <Modal.Header>Edit Make — {editMake?.name}</Modal.Header>
                <Modal.Content>
                    <Form>
                        {modalError && <Message negative content={modalError} />}
                        <Form.Input
                            label="Name"
                            required
                            value={formData.name}
                            error={formErrors.name ? { content: formErrors.name } : false}
                            onChange={(_, { value }) => handleFormChange('name', value)}
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={closeEdit} disabled={saving}>Cancel</Button>
                    <Button primary onClick={handleEdit} loading={saving} disabled={saving}>Save</Button>
                </Modal.Actions>
            </Modal>

            <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} size="mini">
                <Modal.Header>Delete Make</Modal.Header>
                <Modal.Content>
                    <p>Are you sure you want to delete <strong>{makeToDelete?.name}</strong>?</p>
                    <Message warning>
                        <Message.Header>Note</Message.Header>
                        <p>Deleting this make will also delete all of its models.</p>
                    </Message>
                    {modalError && <Message negative content={modalError} />}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setDeleteOpen(false)} disabled={saving}>Cancel</Button>
                    <Button negative onClick={handleDelete} loading={saving} disabled={saving}>Delete</Button>
                </Modal.Actions>
            </Modal>
        </>
    );
};

const AssetModelsPane = ({ makes, models, loading, error, canEdit, onChanged }) => {
    const [createOpen, setCreateOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editModel, setEditModel] = useState(null);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [modelToDelete, setModelToDelete] = useState(null);

    const [formData, setFormData] = useState(EMPTY_MODEL_FORM);
    const [formErrors, setFormErrors] = useState({});
    const [modalError, setModalError] = useState('');
    const [saving, setSaving] = useState(false);

    const makeOptions = useMemo(
        () => makes.map((m) => ({ key: m.id, text: m.name, value: m.id })),
        [makes]
    );

    const handleFormChange = useCallback((field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }, []);

    const validateForm = (data) => {
        const errors = {};
        if (!data.name.trim()) errors.name = 'Name is required';
        return errors;
    };

    const buildPayload = (data) => ({
        name: data.name,
        make: data.make || null,
    });

    const openCreate = () => {
        setFormData(EMPTY_MODEL_FORM);
        setFormErrors({});
        setModalError('');
        setCreateOpen(true);
    };

    const handleCreate = async () => {
        const errors = validateForm(formData);
        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }
        setSaving(true);
        setModalError('');
        try {
            const resp = await createAssetModel(buildPayload(formData));
            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                setFormErrors(extractFieldErrors(errData, ['name', 'make']));
                setModalError(errData.non_field_errors?.[0] || errData.detail || '');
                return;
            }
            await onChanged();
            setCreateOpen(false);
        } catch {
            setModalError('Failed to create model. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const openEdit = (model) => {
        setFormData({ name: model.name, make: model.make || '' });
        setFormErrors({});
        setModalError('');
        setEditModel(model);
        setEditOpen(true);
    };

    const closeEdit = useCallback(() => {
        setEditOpen(false);
        setEditModel(null);
    }, []);

    const handleEdit = async () => {
        const errors = validateForm(formData);
        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }
        setSaving(true);
        setModalError('');
        try {
            const resp = await updateAssetModel(editModel.id, buildPayload(formData));
            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                setFormErrors(extractFieldErrors(errData, ['name', 'make']));
                setModalError(errData.non_field_errors?.[0] || errData.detail || '');
                return;
            }
            await onChanged();
            closeEdit();
        } catch {
            setModalError('Failed to update model. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const openDelete = (model) => {
        setModalError('');
        setModelToDelete(model);
        setDeleteOpen(true);
    };

    const handleDelete = async () => {
        if (!modelToDelete) return;
        setSaving(true);
        try {
            const resp = await deleteAssetModel(modelToDelete.id);
            if (!resp.ok) {
                const errData = await resp.json().catch(() => ({}));
                setModalError(errData.detail || 'Failed to delete model.');
                return;
            }
            await onChanged();
            setDeleteOpen(false);
            setModelToDelete(null);
        } catch {
            setModalError('Failed to delete model. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const columns = useMemo(() => {
        const cols = [
            {
                Header: 'Model',
                accessor: 'name',
                Cell: ({ value }) => <strong>{value}</strong>,
            },
            {
                Header: 'Make',
                accessor: 'makeName',
                Cell: ({ value }) => value || '—',
            },
        ];

        if (canEdit) {
            cols.push({
                Header: 'Actions',
                id: 'actions',
                disableFilters: true,
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <Button
                            size="small"
                            icon="edit"
                            aria-label={`Edit ${row.original.name}`}
                            title="Edit model"
                            onClick={() => openEdit(row.original)}
                        />
                        <Button
                            size="small"
                            icon="trash"
                            color="red"
                            aria-label={`Delete ${row.original.name}`}
                            title="Delete model"
                            onClick={() => openDelete(row.original)}
                        />
                    </div>
                ),
            });
        }

        return cols;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canEdit]);

    return (
        <>
            <div style={headerRowStyle}>
                <Header as="h3" style={{ margin: 0 }}>Models</Header>
                {canEdit && (
                    <Button primary icon labelPosition="left" onClick={openCreate}>
                        <Icon name="plus" />
                        Add Model
                    </Button>
                )}
            </div>

            {error && <Message negative content={error} />}

            <LoadingWrapper loading={loading} text="Loading models...">
                {models.length === 0 ? (
                    <Message info content="No models found." />
                ) : (
                    <ReactTable
                        className="asset-models-table react-table-mobile-compact"
                        data={models}
                        columns={columns}
                        hideGlobalFilter
                    />
                )}
            </LoadingWrapper>

            <Modal open={createOpen} onClose={() => setCreateOpen(false)} size="small">
                <Modal.Header>Add Model</Modal.Header>
                <Modal.Content>
                    <Form>
                        {modalError && <Message negative content={modalError} />}
                        <Form.Input
                            label="Name"
                            required
                            placeholder="e.g. F-150"
                            value={formData.name}
                            error={formErrors.name ? { content: formErrors.name } : false}
                            onChange={(_, { value }) => handleFormChange('name', value)}
                        />
                        <Form.Select
                            label="Make"
                            placeholder="Select make (optional)"
                            clearable
                            search
                            options={makeOptions}
                            value={formData.make}
                            error={formErrors.make ? { content: formErrors.make } : false}
                            onChange={(_, { value }) => handleFormChange('make', value)}
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
                    <Button primary onClick={handleCreate} loading={saving} disabled={saving}>Create</Button>
                </Modal.Actions>
            </Modal>

            <Modal open={editOpen} onClose={closeEdit} size="small">
                <Modal.Header>Edit Model — {editModel?.name}</Modal.Header>
                <Modal.Content>
                    <Form>
                        {modalError && <Message negative content={modalError} />}
                        <Form.Input
                            label="Name"
                            required
                            value={formData.name}
                            error={formErrors.name ? { content: formErrors.name } : false}
                            onChange={(_, { value }) => handleFormChange('name', value)}
                        />
                        <Form.Select
                            label="Make"
                            placeholder="Select make (optional)"
                            clearable
                            search
                            options={makeOptions}
                            value={formData.make}
                            error={formErrors.make ? { content: formErrors.make } : false}
                            onChange={(_, { value }) => handleFormChange('make', value)}
                        />
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={closeEdit} disabled={saving}>Cancel</Button>
                    <Button primary onClick={handleEdit} loading={saving} disabled={saving}>Save</Button>
                </Modal.Actions>
            </Modal>

            <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)} size="mini">
                <Modal.Header>Delete Model</Modal.Header>
                <Modal.Content>
                    <p>Are you sure you want to delete <strong>{modelToDelete?.name}</strong>?</p>
                    {modalError && <Message negative content={modalError} />}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={() => setDeleteOpen(false)} disabled={saving}>Cancel</Button>
                    <Button negative onClick={handleDelete} loading={saving} disabled={saving}>Delete</Button>
                </Modal.Actions>
            </Modal>
        </>
    );
};

export const AssetMakesModels = () => {
    const canEdit = isICCSupport() && !isReadOnly();

    const [makes, setMakes] = useState([]);
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [makesResp, modelsResp] = await Promise.all([getAssetMakes(), getAssetModels()]);
            if (!makesResp.ok || !modelsResp.ok) throw new Error('Failed to load catalog');
            setMakes(await makesResp.json());
            setModels(await modelsResp.json());
        } catch {
            setError('Failed to load makes and models. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    const hasFetchedRef = useRef(false);
    useEffect(() => {
        if (hasFetchedRef.current) return;
        hasFetchedRef.current = true;
        loadData();
    }, [loadData]);

    const panes = [
        {
            menuItem: 'Makes',
            render: () => (
                <Tab.Pane>
                    <AssetMakesPane
                        makes={makes}
                        models={models}
                        loading={loading}
                        error={error}
                        canEdit={canEdit}
                        onChanged={loadData}
                    />
                </Tab.Pane>
            ),
        },
        {
            menuItem: 'Models',
            render: () => (
                <Tab.Pane>
                    <AssetModelsPane
                        makes={makes}
                        models={models}
                        loading={loading}
                        error={error}
                        canEdit={canEdit}
                        onChanged={loadData}
                    />
                </Tab.Pane>
            ),
        },
    ];

    return (
        <Container>
            <Header as="h2" style={{ marginTop: '1rem' }}>Asset Makes &amp; Models</Header>
            <Tab panes={panes} />
            <Footer />
        </Container>
    );
};
